import type { SaveData } from "../domain/types";
import { DEFAULT_SETTINGS, normalizeSettings } from "./settings";
import { repairPlayerProgress } from "./progression";

const KEY = "littlecastle-defense-save-v4";
const METADATA_KEY = "littlecastle-defense-save-metadata-v4";
const LEGACY_KEYS = [
  "littlecastle-defense-save-v3",
  "littlecastle-defense-save-v2",
  "littlecastle-defense-save-v1",
] as const;
const CORRUPT_PREFIX = "littlecastle-defense-corrupt-";

export interface SaveMetadata {
  readonly localRevision: number;
  readonly cloudVersion: number;
  readonly updatedAt: string;
  readonly lastSyncedAt: string | null;
  readonly dirty: boolean;
  readonly lastError: string | null;
}

export interface SaveWriteResult {
  readonly ok: boolean;
  readonly status: "saved" | "storage-unavailable" | "quota-exceeded";
  readonly error: string | null;
}

export interface SaveDiagnostic {
  readonly schemaVersion: number;
  readonly localRevision: number;
  readonly cloudVersion: number;
  readonly dirty: boolean;
  readonly updatedAt: string;
  readonly lastSyncedAt: string | null;
  readonly lastError: string | null;
  readonly storageAvailable: boolean;
  readonly approximateBytes: number;
}

export const DEFAULT_SAVE: SaveData = {
  version: 4,
  progress: {
    unlockedTowers: ["archer", "rocket", "lightning", "cannon"],
    completedLevels: [],
    bestResults: {},
    tutorialComplete: false,
    progressionCurrency: 0,
    royalSeals: 0,
    scenarioStars: {},
    namedLoadouts: {},
    lastScenarioId: "castle-approach:easy",
    achievements: [],
    statistics: { victories: 0, defeats: 0, bossesDefeated: 0, highestWave: 0, totalScore: 0 },
  },
  settings: DEFAULT_SETTINGS,
  loadout: { towerIds: ["archer", "rocket", "lightning", "cannon"], maximumSlots: 5 },
};

const clone = (): SaveData => structuredClone(DEFAULT_SAVE);
const now = (): string => new Date().toISOString();
const defaultMetadata = (): SaveMetadata => ({
  localRevision: 0,
  cloudVersion: 0,
  updatedAt: now(),
  lastSyncedAt: null,
  dirty: false,
  lastError: null,
});

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function isQuotaError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED")
  );
}

function safelyStore(key: string, value: string): SaveWriteResult {
  const target = storage();
  if (!target)
    return {
      ok: false,
      status: "storage-unavailable",
      error: "Browser storage is unavailable.",
    };
  try {
    target.setItem(key, value);
    return { ok: true, status: "saved", error: null };
  } catch (error) {
    return {
      ok: false,
      status: isQuotaError(error) ? "quota-exceeded" : "storage-unavailable",
      error: error instanceof Error ? error.message : "Unable to write browser storage.",
    };
  }
}

export function normalizeSaveData(parsed: Partial<SaveData> | null | undefined): SaveData {
  if (!parsed || typeof parsed !== "object") return clone();
  const rawProgress = {
    ...DEFAULT_SAVE.progress,
    ...(parsed.progress ?? {}),
    completedLevels: Array.isArray(parsed.progress?.completedLevels)
      ? parsed.progress.completedLevels.filter((id): id is string => typeof id === "string")
      : [],
    unlockedTowers: Array.isArray(parsed.progress?.unlockedTowers)
      ? parsed.progress.unlockedTowers.filter((id): id is string => typeof id === "string")
      : DEFAULT_SAVE.progress.unlockedTowers,
    bestResults:
      parsed.progress?.bestResults && typeof parsed.progress.bestResults === "object"
        ? parsed.progress.bestResults
        : {},
    scenarioStars:
      parsed.progress?.scenarioStars && typeof parsed.progress.scenarioStars === "object"
        ? parsed.progress.scenarioStars
        : {},
  };
  const progress = repairPlayerProgress(rawProgress);
  const allowed = new Set(progress.unlockedTowers);
  const savedLoadout = Array.isArray(parsed.loadout?.towerIds)
    ? parsed.loadout.towerIds
        .filter((id): id is string => typeof id === "string" && allowed.has(id))
        .slice(0, 5)
    : [];
  return {
    version: 4,
    settings: normalizeSettings(parsed.settings),
    progress,
    loadout: {
      towerIds: savedLoadout.length > 0 ? savedLoadout : DEFAULT_SAVE.loadout.towerIds,
      maximumSlots: 5,
    },
  };
}

export function loadSave(): SaveData {
  const target = storage();
  if (!target) return clone();
  try {
    const current = target.getItem(KEY);
    const raw = current ?? LEGACY_KEYS.map((key) => target.getItem(key)).find(Boolean);
    if (!raw) return clone();
    const parsed = JSON.parse(raw) as Partial<SaveData>;
    const normalized = normalizeSaveData(parsed);
    if (!current) {
      safelyStore(KEY, JSON.stringify(normalized));
      writeSaveMetadata({ ...defaultMetadata(), dirty: true });
    }
    return normalized;
  } catch {
    try {
      const raw = target.getItem(KEY);
      if (raw) {
        target.setItem(`${CORRUPT_PREFIX}${Date.now()}`, raw);
        target.removeItem(KEY);
      }
    } catch {
      // Recovery must still return a playable save when storage access is blocked.
    }
    writeSaveMetadata({
      ...defaultMetadata(),
      lastError: "A corrupted save was quarantined and replaced with a safe default.",
    });
    return clone();
  }
}

export function readSaveMetadata(): SaveMetadata {
  const target = storage();
  if (!target) return { ...defaultMetadata(), lastError: "Browser storage is unavailable." };
  try {
    const raw = target.getItem(METADATA_KEY);
    if (!raw) return defaultMetadata();
    const parsed = JSON.parse(raw) as Partial<SaveMetadata>;
    return {
      localRevision: Math.max(0, Math.floor(Number(parsed.localRevision) || 0)),
      cloudVersion: Math.max(0, Math.floor(Number(parsed.cloudVersion) || 0)),
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : now(),
      lastSyncedAt: typeof parsed.lastSyncedAt === "string" ? parsed.lastSyncedAt : null,
      dirty: parsed.dirty === true,
      lastError: typeof parsed.lastError === "string" ? parsed.lastError : null,
    };
  } catch {
    return { ...defaultMetadata(), lastError: "Save metadata was invalid and was repaired." };
  }
}

export function writeSaveMetadata(metadata: SaveMetadata): SaveWriteResult {
  return safelyStore(METADATA_KEY, JSON.stringify(metadata));
}

export function writeSave(
  save: SaveData,
  options: { readonly markDirty?: boolean } = {},
): SaveWriteResult {
  const normalized = normalizeSaveData(save);
  const result = safelyStore(KEY, JSON.stringify(normalized));
  if (!result.ok) return result;
  const previous = readSaveMetadata();
  const markDirty = options.markDirty ?? true;
  const metadataResult = writeSaveMetadata({
    ...previous,
    localRevision: markDirty ? previous.localRevision + 1 : previous.localRevision,
    updatedAt: markDirty ? now() : previous.updatedAt,
    dirty: markDirty ? true : previous.dirty,
    lastError: null,
  });
  return metadataResult.ok ? result : metadataResult;
}

export function markSaveSynchronized(cloudVersion: number): SaveWriteResult {
  const previous = readSaveMetadata();
  return writeSaveMetadata({
    ...previous,
    cloudVersion: Math.max(0, Math.floor(cloudVersion)),
    lastSyncedAt: now(),
    dirty: false,
    lastError: null,
  });
}

export function recordSaveError(message: string): void {
  writeSaveMetadata({ ...readSaveMetadata(), lastError: message });
}

export function exportSaveData(): string {
  return JSON.stringify(
    {
      format: "littlecastle-defense-save",
      schemaVersion: 4,
      exportedAt: now(),
      save: loadSave(),
    },
    null,
    2,
  );
}

export function importSaveData(serialized: string): SaveWriteResult {
  try {
    const parsed = JSON.parse(serialized) as unknown;
    const record =
      typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    const candidate = record?.format === "littlecastle-defense-save" ? record.save : parsed;
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate))
      return { ok: false, status: "storage-unavailable", error: "Import is not a valid save." };
    const save = candidate as Partial<SaveData>;
    if (!save.progress || !save.settings || !save.loadout)
      return {
        ok: false,
        status: "storage-unavailable",
        error: "Import does not contain a complete Defense campaign save.",
      };
    return writeSave(normalizeSaveData(save));
  } catch (error) {
    return {
      ok: false,
      status: "storage-unavailable",
      error: error instanceof Error ? error.message : "Import is not valid JSON.",
    };
  }
}

export function saveDiagnostics(): SaveDiagnostic {
  const target = storage();
  const metadata = readSaveMetadata();
  let approximateBytes = 0;
  try {
    approximateBytes = target?.getItem(KEY)?.length ?? 0;
  } catch {
    // Diagnostics remain available when storage access is blocked.
  }
  return {
    schemaVersion: 4,
    ...metadata,
    storageAvailable: target !== null,
    approximateBytes,
  };
}

export function clearSave(): void {
  const target = storage();
  if (!target) return;
  target.removeItem(KEY);
  target.removeItem(METADATA_KEY);
  for (const key of LEGACY_KEYS) target.removeItem(key);
}
